/**
 * Word Convergence Animation Variant (Web)
 *
 * "Thoughts crystallizing into text" - Letters scatter chaotically then
 * converge into words with chromatic aberration effects.
 */

import { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { View, Text, Pressable, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import type { LetterThrowingAnimationProps, AnimationPhase } from './types';
import { PHASE_TIMING } from './types';
import { useSpeedPresets } from './hooks/useSpeedPresets';
import { getCategoryFromScore, getCategoryStyle } from '../../lib/categoryStyles';
import {
  SCREEN_WIDTH,
  getPhaseFromProgress,
  getConvergenceSubPhase,
  calculateLetterPositions,
  updateConvergingLetter,
  buildFormedTextFromPositions,
  animateBadgeReveal,
  REVELATION_START,
  ANIMATION_BG_GRADIENT,
  type ConvergingLetterState,
} from './animationHelpers.web';
import { styles } from './animationStyles.web';
import { useReducedMotionFallback } from './hooks/useReducedMotionFallback';
import ConvergingLettersView from './ConvergingLettersView.web';
import DramaBadgeView from './DramaBadgeView.web';

const WordConvergenceAnimation = memo(function WordConvergenceAnimation({
  text,
  speed = '3',
  onComplete,
  onSkip,
  style,
  analysisResult,
}: LetterThrowingAnimationProps) {
  const { t } = useTranslation('home');
  const { settings, wordsForLine } = useSpeedPresets(speed);
  const prefersReducedMotion = useReducedMotionFallback();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [subPhase, setSubPhase] = useState<'chaos' | 'converging' | 'lineForming' | 'transitioning'>('chaos');
  const [showSkipHint, setShowSkipHint] = useState(false);
  const [lineComplete, setLineComplete] = useState(false);
  const [formedText, setFormedText] = useState('');
  const [convergingLetters, setConvergingLetters] = useState<ConvergingLetterState[]>([]);

  const [badgeScale] = useState(new Animated.Value(0));
  const [badgeOpacity] = useState(new Animated.Value(0));

  const rafIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const skipRequestedRef = useRef(false);
  const letterPositionsRef = useRef<ReturnType<typeof calculateLetterPositions>>([]);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const badgeRevealedRef = useRef(false);
  const lineCompleteRef = useRef(false);

  const category = analysisResult?.category || getCategoryFromScore(analysisResult?.score || 50);
  const categoryStyle = getCategoryStyle(category);

  const totalDuration = useMemo(() => 12000 / settings.multiplier, [settings.multiplier]);

  useEffect(() => {
    const containerWidth = SCREEN_WIDTH * 0.8;
    letterPositionsRef.current = calculateLetterPositions(text, containerWidth, wordsForLine);
    setConvergingLetters(letterPositionsRef.current.map((pos, idx) => ({
      ...pos,
      globalIndex: idx,
      startX: (Math.random() - 0.5) * SCREEN_WIDTH * 1.5,
      startY: (Math.random() - 0.5) * 400,
      currentX: (Math.random() - 0.5) * SCREEN_WIDTH * 1.5,
      currentY: (Math.random() - 0.5) * 400,
      scale: 40 + Math.random() * 20,
      opacity: 0.2 + Math.random() * 0.3,
      chromaOffset: settings.chromaOffset * (1 + Math.random()),
      landed: false,
    })));
  }, [text, wordsForLine, settings.chromaOffset]);

  useEffect(() => {
    if (prefersReducedMotion) {
      setFormedText(letterPositionsRef.current.map(l => l.char).join(''));
      setProgress(1);
      setLineComplete(true);
      lineCompleteRef.current = true;
      onComplete?.();
      return;
    }

    startTimeRef.current = performance.now();
    setPhase('emergence');

    const animate = (currentTime: number) => {
      if (skipRequestedRef.current) return;
      const elapsed = currentTime - startTimeRef.current;
      const newProgress = Math.min(elapsed / totalDuration, 1);
      setProgress(newProgress);
      setPhase(getPhaseFromProgress(newProgress));

      if (newProgress <= PHASE_TIMING.emergence) {
        const ep = newProgress / PHASE_TIMING.emergence;
        const csp = getConvergenceSubPhase(ep);
        setSubPhase(csp);

        setConvergingLetters(prev => prev.map((letter, idx) => {
          const tp = letterPositionsRef.current[idx];
          return tp ? updateConvergingLetter(letter, tp, csp, ep, settings.chromaOffset) : letter;
        }));

        if ((csp === 'lineForming' || csp === 'transitioning') && !lineCompleteRef.current) {
          lineCompleteRef.current = true;
          setLineComplete(true);
          setFormedText(buildFormedTextFromPositions(letterPositionsRef.current));
        }
      }

      if (newProgress < 1) rafIdRef.current = requestAnimationFrame(animate);
      else completionTimerRef.current = setTimeout(() => onComplete?.(), 600);
    };

    rafIdRef.current = requestAnimationFrame(animate);
    const hintTimeout = setTimeout(() => setShowSkipHint(true), 1000);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      clearTimeout(hintTimeout);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    };
  }, [totalDuration, settings, onComplete, prefersReducedMotion]);

  useEffect(() => {
    if (badgeRevealedRef.current) return;
    if (progress >= REVELATION_START && analysisResult) {
      badgeRevealedRef.current = true;
      animateBadgeReveal(badgeScale, badgeOpacity).start();
    }
  }, [progress, analysisResult, badgeScale, badgeOpacity]);

  const handleSkip = useCallback(() => {
    skipRequestedRef.current = true;
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    setFormedText(buildFormedTextFromPositions(letterPositionsRef.current));
    setProgress(1);
    setPhase('complete');
    setLineComplete(true);
    lineCompleteRef.current = true;
    onSkip?.();
  }, [onSkip]);

  const phaseLabel = useMemo(() => {
    if (phase === 'emergence') {
      return subPhase === 'chaos' ? t('home.modern.processing') + '...' : t('home.modern.processing');
    }
    return phase !== 'complete' ? t('home.modern.processing') : '';
  }, [phase, subPhase, t]);

  return (
    <Pressable onPress={handleSkip} style={[styles.container, style]} accessibilityRole="button" accessibilityLabel={t('home.modern.skip_animation')}>
      <LinearGradient colors={ANIMATION_BG_GRADIENT} style={styles.gradient}>
        <View style={styles.content}>
          <ConvergingLettersView letters={convergingLetters} lineComplete={lineComplete} phase={phase} />
          {lineComplete && (
            <View style={styles.formedTextContainer}>
              <Text style={styles.formedText}>{formedText}</Text>
            </View>
          )}
          <DramaBadgeView analysisResult={analysisResult} badgeOpacity={badgeOpacity} badgeScale={badgeScale} categoryStyle={categoryStyle} />
          <View style={styles.progressContainer}>
            <View style={[styles.progressBar, { width: `${progress * 100}%` }]} />
          </View>
          {phaseLabel && <Text style={styles.phaseLabel}>{phaseLabel}</Text>}
          {showSkipHint && phase !== 'complete' && <Text style={styles.skipHint}>{t('home.modern.tap_to_skip')}</Text>}
        </View>
      </LinearGradient>
    </Pressable>
  );
});

export default WordConvergenceAnimation;
